/**
 * Dünner EventSource-Wrapper für den Agent-Live-Feed. Bewusst minimal: parst die
 * benannten SSE-Frames und ruft Callbacks — die eigentliche State-Logik (Dedupe,
 * Cap, Reducer) liegt testbar in `agent-live.ts`. EventSource übernimmt das
 * automatische Reconnect; bei `error` meldet der Browser selbst neue Versuche.
 *
 * Auth läuft über das Session-Cookie (same-origin); kein HMAC, kein Token.
 */

import type { LiveEventFrame, LiveHeartbeatFrame, LiveStatus } from "./agent-live";

export interface AgentLiveHandlers {
  onStatus: (s: LiveStatus) => void;
  onReady: (data: { server_time?: number }) => void;
  onBackfill: (events: LiveEventFrame[]) => void;
  onEvent: (ev: LiveEventFrame) => void;
  onHeartbeat: (hb: LiveHeartbeatFrame) => void;
}

function safeParse<T>(raw: string): T | null {
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export class AgentLiveClient {
  private es: EventSource | null = null;

  constructor(private url: string, private handlers: AgentLiveHandlers) {}

  connect(): void {
    if (typeof EventSource === "undefined") return; // SSR/Tests: no-op
    this.handlers.onStatus("connecting");
    const es = new EventSource(this.url);

    es.addEventListener("ready", (e) => {
      this.handlers.onStatus("connected");
      const data = safeParse<{ server_time?: number }>((e as MessageEvent).data);
      if (data) this.handlers.onReady(data);
    });
    es.addEventListener("backfill", (e) => {
      const data = safeParse<{ events?: LiveEventFrame[] }>((e as MessageEvent).data);
      if (data && Array.isArray(data.events)) this.handlers.onBackfill(data.events);
    });
    es.addEventListener("agent_event", (e) => {
      const ev = safeParse<LiveEventFrame>((e as MessageEvent).data);
      if (ev) this.handlers.onEvent(ev);
    });
    es.addEventListener("heartbeat", (e) => {
      const hb = safeParse<LiveHeartbeatFrame>((e as MessageEvent).data);
      if (hb) this.handlers.onHeartbeat(hb);
    });
    es.addEventListener("ping", () => {
      /* keep-alive only */
    });

    es.onopen = () => this.handlers.onStatus("connected");
    // EventSource versucht selbst zu reconnecten; wir zeigen nur den Status.
    es.onerror = () => this.handlers.onStatus("error");

    this.es = es;
  }

  close(): void {
    if (this.es) {
      this.es.close();
      this.es = null;
    }
    this.handlers.onStatus("closed");
  }
}
